<template>
  <div class="base-autocomplete" :class="{hasError, hasSuccess, [`base-autocomplete--open`]: isOpen, isDisabled: ($attrs.disabled!==undefined && $attrs.disabled!==false)}">
    <PflegeInput
      v-bind="$attrs"
      v-model="inputValue"
      :label="label"
      :has-error="hasError"
      :error-message="errorMessage"
      :has-success="hasSuccess"
      :persistent-hint="true"
      autocomplete="new-password"
      @update:modelValue="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keyup.up="onKeyUp"
      @keyup.down="onKeyDown"
      @keyup.enter="onEnter"
    >
      <template #hint>
        <slot name="hint"></slot>
      </template>
    </PflegeInput>

    <div class="base-autocomplete__dropdown pflegescrollbar">
      <ul>
        <li
          v-for="(option, i) in filteredOptions"
          :key="i"
          :class="[`base-autocomplete__dropdown-option`, { [`base-autocomplete__dropdown-option--active`]: activeOption === i }]"
          @mouseover="activeOption = i"
          @mousedown="onSelecting(option)"
        >
          {{ getOptionLabel(option) }}
        </li>
      </ul>
    </div>
  </div>
</template>


<script setup>
import { ref, watch } from 'vue'
import PflegeInput from '../Input'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: {
    type: [String, Object, Number],
  },
  options: {
    type: Array,
    required: true,
  },
  optionLabel: {
    type: String,
    default: 'label',
  },
  optionValue: {
    type: String,
    default: 'value',
  },
  label: {
    type: String,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  hasSuccess: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: null,
  },
  returnObject: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'focus',
  'keyup.up',
  'keyup.down',
  'keyup.enter',
])

let defaultInputValue = ''
if (typeof props.modelValue === 'string') {
  const match = props.options.filter((option) => option[props.optionValue] === props.modelValue)
  defaultInputValue = match.length > 0 ? match[0][props.optionLabel] : ''
} else {
  if(props.modelValue){
    defaultInputValue = props.modelValue[props.optionLabel] || ''
  }else{
    defaultInputValue = ''
  }
}

const inputValue = ref(defaultInputValue)
const isOpen = ref(false)
const filteredOptions = ref(props.options)
const activeOption = ref(null)

function onFocus(e) {
  isOpen.value = true
  emit('focus', e)
}

function onBlur(e) {
  emit('blur', e)
  isOpen.value = false
}

function onInput(e) {
  emit('update:modelValue', e)
}

function onKeyDown(e) {
  if (activeOption.value !== null) {
    if (activeOption.value < props.options.length - 1) {
      activeOption.value += 1
    }
  } else {
    activeOption.value = 0
  }
  emit('keyup.down', e)
}

function onKeyUp(e) {
  if (activeOption.value) {
    activeOption.value -= 1
  }
  emit('keyup.up', e)
}

function onEnter(e) {
  if (filteredOptions.value.length > 0) {
    onSelecting(filteredOptions.value[activeOption.value || 0])
  }
  emit('keyup.enter', e)
}

function getOptionLabel(option){
  if(option[props.optionLabel]){
    return option[props.optionLabel]
  };
  console.warn(`Autocomplete: given optionLabel "${props.optionLabel}" could not be found on chosen option :>> `, option)
  return option.label ? option.label : JSON.stringify(option)
}

function getOptionValue(option){
  if(props.returnObject){
    return option
  };
  if(option[props.optionValue]){
    return option[props.optionValue]
  };
  console.warn(`Autocomplete: given optionValue "${props.optionValue}" could not be found on chosen option :>> `, option)
  return option.value ? option.value : option
}

function onSelecting(option) {
  inputValue.value = getOptionLabel(option)
  emit('update:modelValue', getOptionValue(option))
}

function filterOptions() {
  filteredOptions.value = props.options.filter((option) => getOptionLabel(option).toLowerCase().includes(inputValue.value.toLowerCase()))
  activeOption.value = null
  isOpen.value = filteredOptions.value.length > 0 && inputValue.value !== filteredOptions.value[0][props.optionLabel]
}
watch(
  () => inputValue.value,
  () => filterOptions(),
)

watch(
  () => props.options, (asyncOptions) => {
    filteredOptions.value = asyncOptions
  },
)
</script>



<style lang="scss">
@use '../../../styles' as index;


.base-autocomplete {
  $block: &;
  $m-open: #{$block}--open;
  position: relative;

  &__dropdown {
    display: none;
    background-color: var(--white);
    border: 1px solid var(--mint-darker);
    border-radius: 0 0 .25rem .25rem;
    overflow: hidden auto;
    cursor: pointer;
    position: absolute;
    top: 4rem;
    left: 0;
    z-index: 10;
    width: 100%;
    max-height: 15rem;

    #{$m-open} & {
      display: flex;
    }

    ul {
      padding: 0;
      margin: 0;
      width: 100%;
    }

    li {
      list-style-type: none;
    }
  }

  &__dropdown-option {
    white-space: normal;
    padding: .85rem .75rem;
    color: index.$color__tertiaryDark;
    transition: color index.$animation__transition--fast, background-color index.$animation__transition--fast;

    &--active {
      background-color: index.$color__secondaryMint--dark;
      color: index.$color__white;
    }
  }
}
</style>
